/**
 * Stato di disponibilità della squadra, letto dal backend invece che dedotto.
 *
 * Prima la card faceva `atleti totali - infortunati`: su una rosa che non ha
 * compilato nulla scriveva "24/24 disponibili · Tutti disponibili", mentre
 * `GET /teams/pulse` sullo stesso team rispondeva `available: 0,
 * noResponse: 24`. Due definizioni di "disponibile" nello stesso prodotto, e
 * quella mostrata all'allenatore era la più ottimista.
 *
 * Il backend è la fonte: disponibile = ha compilato l'Hooper ed è risultato
 * fuori rischio. Senza compilazione il rischio non è noto, e "non lo so" non
 * si arrotonda a "sta bene".
 */
import { useQuery } from "@tanstack/react-query";
import { useEndpoints } from "../../../../hooks/useEndpoints";
import { ITeamPulse } from "../../../../models/endpoints/Team";

export function useAvailabilityCard(teamId?: string) {
  const endpoints = useEndpoints();

  const pulseQuery = useQuery<ITeamPulse>({
    queryKey: ["team-pulse", teamId],
    queryFn: async () => {
      const { data } = await endpoints.teamPulse(teamId!);
      return data;
    },
    enabled: Boolean(teamId),
    staleTime: 60_000,
  });

  const pulse = pulseQuery.data;
  const totale = pulse
    ? pulse.available + pulse.atRisk + pulse.injured + pulse.noResponse
    : 0;

  return {
    pulse,
    totale,
    isLoading: pulseQuery.isPending,
    /** Nessuno ha compilato: la card lo dice invece di dichiararli tutti sani. */
    nessunaCompilazione: Boolean(pulse && totale > 0 && pulse.noResponse === totale),
    tuttiDisponibili: Boolean(pulse && totale > 0 && pulse.available === totale),
  };
}
